/**
 * 冒険メニュー用線画アイコン
 * 冒険メニュー全体で読みやすい統一アイコンを提供します。
 */

import React from "react";

/** 冒険メニューアイコン種別 */
export type AdventureMenuIconName =
  | "group"
  | "map"
  | "notice"
  | "ranking"
  | "results"
  | "stall";

/** 冒険メニューアイコンのプロパティ */
type AdventureMenuIconProps = {
  className?: string;
  name: AdventureMenuIconName;
  size?: number;
};

/**
 * 冒険メニュー用の統一線画アイコンを表示する
 * @param {AdventureMenuIconProps} props - プロパティ
 * @returns {JSX.Element} SVGアイコン
 */
const AdventureMenuIcon = ({
  className = "",
  name,
  size = 28,
}: AdventureMenuIconProps): React.JSX.Element => {
  /** 共通SVG属性 */
  const commonProps = {
    "aria-hidden": true,
    className,
    fill: "none",
    focusable: "false" as const,
    height: size,
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    strokeWidth: 1.8,
    viewBox: "0 0 24 24",
    width: size,
  };

  if (name === "stall") {
    return (
      <svg {...commonProps}>
        <path d="M4 9h16l-1.6-4H5.6L4 9Z" />
        <path d="M5 9v10h14V9M8 19v-5h4v5" />
        <path d="M4 9c0 1.2 1 2.1 2.2 2.1S8.4 10.2 8.4 9c0 1.2 1 2.1 2.2 2.1S12.8 10.2 12.8 9c0 1.2 1 2.1 2.2 2.1s2.2-.9 2.2-2.1c0 1.2 1 2.1 2.2 2.1" />
        <path d="m16.5 14 .45.9 1 .15-.72.7.17 1-.9-.48-.9.48.17-1-.72-.7 1-.15.45-.9Z" />
      </svg>
    );
  }

  if (name === "ranking") {
    return (
      <svg {...commonProps}>
        <path d="M8 20H4v-6h4v6Zm6 0h-4V9h4v11Zm6 0h-4v-9h4v9Z" />
        <path d="m12 3 .8 1.65 1.82.26-1.31 1.28.31 1.81L12 7.15 10.38 8l.31-1.81-1.31-1.28 1.82-.26L12 3Z" />
      </svg>
    );
  }

  if (name === "group") {
    return (
      <svg {...commonProps}>
        <circle cx="12" cy="8" r="3" />
        <circle cx="5.5" cy="10.5" r="2.25" />
        <circle cx="18.5" cy="10.5" r="2.25" />
        <path d="M7.5 20v-1.6a4.5 4.5 0 0 1 9 0V20M2 20v-1.1a3.5 3.5 0 0 1 4.2-3.43M22 20v-1.1a3.5 3.5 0 0 0-4.2-3.43" />
      </svg>
    );
  }

  if (name === "map") {
    return (
      <svg {...commonProps}>
        <path d="m3.5 6 5-2 7 2 5-2v14l-5 2-7-2-5 2V6Z" />
        <path d="M8.5 4v14M15.5 6v14" />
        <path d="M12 8.2a2.15 2.15 0 0 1 2.15 2.15c0 1.65-2.15 3.65-2.15 3.65s-2.15-2-2.15-3.65A2.15 2.15 0 0 1 12 8.2Z" />
        <circle cx="12" cy="10.35" r=".55" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  if (name === "notice") {
    return (
      <svg {...commonProps}>
        <path d="M6.5 9a5.5 5.5 0 0 1 11 0c0 6 2.5 6.5 2.5 6.5H4S6.5 15 6.5 9Z" />
        <path d="M9.5 18a2.5 2.5 0 0 0 5 0M12 3V1.8" />
        <path d="m18.3 4.2.9-.9M5.7 4.2l-.9-.9" />
      </svg>
    );
  }

  if (name === "results") {
    return (
      <svg {...commonProps}>
        <path d="M8 4h8v3.5a4 4 0 0 1-8 0V4Z" />
        <path d="M8 6H4.5v1.2A3.3 3.3 0 0 0 8.1 10M16 6h3.5v1.2a3.3 3.3 0 0 1-3.6 2.8M12 11.5V16M8.5 20h7M10 16h4v4" />
        <path d="m12 5.3.5 1 1.1.16-.8.78.2 1.1-1-.52-1 .52.2-1.1-.8-.78 1.1-.16.5-1Z" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path d="M8 3h8v3H8V3Zm-3 5h14v3H5V8Zm2 5h10v8H7v-8Z" />
      <path d="M10 15h4v4h-4z" />
    </svg>
  );
};

export default AdventureMenuIcon;

